// The red banner's words (walkthrough 10): an unauthorized fill — a broker
// order that matches no order of a live sync plan of the current target —
// said as what traded, when, and why it's flagged. Every figure is the
// API's decimal string, formatted as a string (lib/money). The symbol,
// broker order id and ref_id come from the executor (an agent), and the
// server's detail sentence quotes them: bidi controls are stripped.

import { withoutBidiControls as plain } from "../../snippets/bidi";
import { formatInstant } from "./format";
import { formatShares, formatUsd, scaled } from "./money";
import { fillStateLabel } from "./target";

/** Each §3.2 condition (InvestingFillReasonEnum) in plain words. */
export const UNAUTHORIZED_REASONS = {
  approval_off: "Robinhood's trade approval read OFF when it was reported — no order is placed while it's off.",
  no_ref_id: "It carries no ref_id, so it wasn't placed from a sync plan.",
  unknown_ref_id: "Its ref_id is no order of any sync plan.",
  symbol_mismatch: "It's in a different stock from the plan order its ref_id names.",
  side_mismatch: "It's the opposite side — a buy for a sell, or a sell for a buy — of the plan order its ref_id names.",
  target_superseded: "Its plan was for a target that's no longer the current one.",
  plan_not_live: "Its plan wasn't open or executing — only a live plan's orders are placed.",
  over_quantity: "More shares filled than its plan order asked for.",
  over_amount: "More dollars filled than its plan order asked for — more than 2% over.",
  no_fill_recorded: "A snapshot lists it at the broker, and no fill was ever recorded for it.",
  snapshot_mismatch: "A snapshot lists it as a different order from the one its fill recorded.",
};

const UNKNOWN_REASON = "It matches no order of a live sync plan of the current target.";

/** Shares filled, as a BigInt of hundred-millionths; null when unknown. */
function filled(fill) {
  return scaled(fill.quantity, 8);
}

/**
 * What traded: "Bought 3 shares of NVDA at $188.10 ($564.30)"; an order
 * with nothing filled says its state; a placeholder (no fill report
 * reached it) says so.
 */
export function fillWhat(fill) {
  const symbol = plain(fill.symbol);
  const shares = filled(fill);
  const state = fillStateLabel(fill.state).toLowerCase();
  if (shares === null) {
    return `A ${fill.side} of ${symbol} — ${state} at the broker; no fill recorded`;
  }
  if (shares === 0n) {
    const working = ["queued", "confirmed"].includes(fill.state);
    return `A ${fill.side} of ${symbol} — ${state}, nothing filled${working ? " yet" : ""}`;
  }
  const count = formatShares(fill.quantity);
  const noun = count === "1" ? "share" : "shares";
  const verb = fill.side === "sell" ? "Sold" : "Bought";
  const price = formatUsd(fill.average_price);
  const amount = formatUsd(fill.amount);
  let text = `${verb} ${count} ${noun} of ${symbol}`;
  if (price) text += ` at ${price}`;
  if (amount) text += ` (${amount})`;
  if (fill.state === "partially_filled") text += " — partly filled, still working";
  return text;
}

/** When: "filled <instant>", or "recorded <instant>" before any shares filled. */
export function fillWhen(fill, timeZone) {
  if (fill.filled_at) return `filled ${formatInstant(fill.filled_at, timeZone)}`;
  return `recorded ${formatInstant(fill.created_at, timeZone)}`;
}

/** Why it's flagged: `{reason}` in words, `{detail}` the server's sentence. */
export function fillWhy(fill) {
  return {
    reason: UNAUTHORIZED_REASONS[fill.unauthorized_reason] ?? UNKNOWN_REASON,
    detail: plain(fill.unauthorized_detail ?? ""),
  };
}

// The server's sentence when an acknowledged fill fills further and shows
// red again (backend/investing/fills.py ACKNOWLEDGED_BEFORE), appended to
// the detail. The API has no field for it: read from the end of the detail.
const RAISED_AGAIN = / acknowledged it at \d{4}-\d{2}-\d{2} \d{2}:\d{2} UTC, with [^;]* shares filled; it has filled further since\.$/;

/** An acknowledged fill that filled further, so it shows red again. */
export function raisedAgain(fill) {
  return RAISED_AGAIN.test(fill.unauthorized_detail ?? "");
}
